/** Footer: what this thing does with your data, and where the data comes from. */

import { catalog$, refreshCatalog } from "../data/catalog.ts";
import { clearIgnored, ignoredCount } from "../engine/dedupe.ts";
import { clearAllStorage } from "../state/store.ts";
import { recomputeAll } from "../engine/scheduler.ts";
import { append, card, el, on } from "./dom.ts";
import { relativeTime } from "../util/format.ts";

function bullet(lead: string, rest: string): HTMLElement {
  return el(
    "li",
    { class: "mb-1" },
    el("span", { class: "fw-semibold", text: lead }),
    " ",
    el("span", { text: rest }),
  );
}

export function createFooter(): HTMLElement {
  const catalogLine = el("span", { class: "tiny text-body-secondary" });

  const refreshBtn = el("button", {
    type: "button",
    class: "btn btn-sm btn-outline-secondary",
    text: "Refresh catalog",
  });
  on(refreshBtn, "click", () => {
    refreshBtn.disabled = true;
    refreshBtn.textContent = "Refreshing…";
    void refreshCatalog(true).finally(() => {
      refreshBtn.disabled = false;
      refreshBtn.textContent = "Refresh catalog";
    });
  });

  const clearIgnoredBtn = el("button", { type: "button", class: "btn btn-sm btn-outline-secondary" });
  on(clearIgnoredBtn, "click", () => {
    clearIgnored();
    recomputeAll();
    syncButtons();
  });

  const resetBtn = el("button", {
    type: "button",
    class: "btn btn-sm btn-outline-danger",
    text: "Clear all local data",
  });
  on(resetBtn, "click", () => {
    if (!window.confirm("Remove your keys, watchlist and settings from this browser?")) return;
    clearAllStorage();
    window.location.reload();
  });

  function syncButtons(): void {
    const count = ignoredCount();
    clearIgnoredBtn.textContent = `Clear ignored (${count})`;
    clearIgnoredBtn.disabled = count === 0;
  }

  catalog$.subscribe((state) => {
    catalogLine.textContent = state.loading
      ? "Loading item catalog…"
      : state.error
        ? `Catalog error: ${state.error}`
        : state.items.length === 0
          ? "Item catalog not loaded."
          : `${state.items.length.toLocaleString()} tradable items cached · updated ${relativeTime(state.fetchedAt)}`;
  });

  syncButtons();

  const body = el(
    "div",
    {},
    el(
      "ul",
      { class: "list-unstyled tiny text-body-secondary mb-3" },
      bullet("100% client-side.", "There is no backend — this app runs entirely in your browser."),
      bullet(
        "Your API keys stay on this device.",
        "They live in this browser's localStorage and are sent only to api.torn.com over HTTPS.",
      ),
      bullet(
        "Bazaar data comes from weav3r.dev.",
        "Only the item id is sent — no key, and nothing that identifies you.",
      ),
      bullet("No analytics, no tracking, no cookies.", "Nothing about you or your watchlist is logged or transmitted."),
      bullet("Use a Public key.", "This only needs read access to items and market listings — never a Full or Limited key."),
      bullet(
        "Heads up:",
        "anything in localStorage is readable by browser extensions and any script on this origin. Avoid shared machines.",
      ),
    ),
    el(
      "div",
      { class: "d-flex flex-wrap align-items-center gap-2" },
      refreshBtn,
      clearIgnoredBtn,
      resetBtn,
    ),
    el("div", { class: "mt-2" }, catalogLine),
  );

  const footer = el("footer", { class: "mt-4" });
  append(
    footer,
    card("Privacy & data", body),
    el(
      "p",
      { class: "text-center tiny text-body-secondary mt-3 mb-0" },
      "Shortcut: press ",
      el("kbd", { text: "B" }),
      " to open the best active green deal.",
    ),
  );

  return footer;
}
